import React, { useState } from 'react';
import { Star, CheckCircle, Shield, MessageCircle, Lock, Copy, RefreshCw } from 'lucide-react';

export const ServiceProviderSystem = ({ onClose, selectedService }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [providerProfile, setProviderProfile] = useState(null);
  const [verificationCodes, setVerificationCodes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Demo service providers
  const demoProviders = [
    { 
      id: 1, 
      username: '@fitcoach_sarah', 
      avatar: 'S', 
      serviceName: "Sarah's Fitness Transformation",
      verified: true,
      totalReviews: 234,
      averageRating: 4.7
    },
    { 
      id: 2, 
      username: '@businessmentor_alex', 
      avatar: 'A', 
      serviceName: "Scale Your Business Academy",
      verified: true,
      totalReviews: 189,
      averageRating: 4.2
    },
    { 
      id: 3, 
      username: '@mindful_maya', 
      avatar: 'M', 
      serviceName: "Mindfulness & Meditation Course",
      verified: true,
      totalReviews: 412,
      averageRating: 4.9
    }
  ];

  // Demo reviews
  const demoReviews = [
    {
      id: 1,
      user: '@john_doe',
      rating: 5,
      comment: "Amazing results in just 8 weeks! Sarah really knows her stuff and the personalized approach made all the difference.",
      date: '2 hours ago',
      verified: true,
      verificationCode: 'FIT2024'
    },
    {
      id: 2,
      user: '@business_owner',
      rating: 5,
      comment: "Helped me double my revenue in 6 months. The strategies were practical and easy to implement.",
      date: '1 day ago',
      verified: true,
      verificationCode: 'BUS2024'
    },
    {
      id: 3,
      user: '@startup_founder',
      rating: 4,
      comment: "Great insights and practical advice. Would definitely recommend for anyone looking to scale their business.",
      date: '3 days ago',
      verified: true,
      verificationCode: 'BUS2024'
    }
  ];

  const handleProviderLogin = (provider) => {
    setProviderProfile(provider);
    setIsLoggedIn(true);
    setReviews(demoReviews.filter(review => review.verificationCode.startsWith(provider.username.charAt(1))));
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setProviderProfile(null);
    setVerificationCodes([]);
    setReviews([]);
  };

  const generateVerificationCode = () => {
    const prefix = providerProfile.username.charAt(1).toUpperCase();
    const year = '2024';
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const code = `${prefix}${year}${random}`;
    
    setVerificationCodes(prev => [...prev, {
      id: Date.now(),
      code,
      createdAt: new Date().toISOString(),
      used: false,
      usedBy: null
    }]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Code copied to clipboard!');
  };

  if (!selectedService) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Service Provider Dashboard</h2>
              <p className="text-gray-600 mt-1">Manage your service: {selectedService.instagramHandle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {!isLoggedIn ? (
            /* Provider Login Section */
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Service Provider Login</h3>
                <p className="text-gray-600">Choose a demo service provider to manage</p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {demoProviders.map(provider => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderLogin(provider)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">{provider.avatar}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800">{provider.serviceName}</h4>
                        <p className="text-green-600 text-sm">{provider.username}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1">
                            <Star className="text-yellow-400" size={16} />
                            <span className="text-sm">{provider.averageRating}</span>
                          </div>
                          <span className="text-sm text-gray-500">{provider.totalReviews} reviews</span>
                          {provider.verified && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="text-green-500" size={14} />
                              <span className="text-xs text-green-600">Verified</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="text-blue-600 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">Service Provider Features</h4>
                    <p className="text-sm text-blue-700">
                      Generate verification codes, manage reviews, and track your service performance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Provider Dashboard */
            <div className="space-y-6">
              {/* Provider Info */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xl">{providerProfile.avatar}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{providerProfile.serviceName}</h3>
                    <p className="text-green-600">{providerProfile.username}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1">
                        <Star className="text-yellow-400" size={16} />
                        <span className="font-semibold">{providerProfile.averageRating}</span>
                        <span className="text-sm text-gray-500">({providerProfile.totalReviews} reviews)</span>
                      </div>
                      {providerProfile.verified && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="text-green-500" size={14} />
                          <span className="text-sm text-green-600">Verified Provider</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Switch Provider
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                {[
                  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
                  { id: 'codes', label: 'Verification Codes', icon: '🔐' },
                  { id: 'reviews', label: 'Reviews', icon: '⭐' },
                  { id: 'analytics', label: 'Analytics', icon: '📈' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-white text-gray-800 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Star className="text-green-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Average Rating</p>
                          <p className="text-2xl font-bold text-gray-800">{providerProfile.averageRating}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <MessageCircle className="text-blue-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Reviews</p>
                          <p className="text-2xl font-bold text-gray-800">{providerProfile.totalReviews}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="text-purple-600" size={24} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Verified Reviews</p>
                          <p className="text-2xl font-bold text-gray-800">{reviews.filter(r => r.verified).length}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
                    <div className="space-y-4">
                      {reviews.slice(0, 3).map(review => (
                        <div key={review.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {review.user.charAt(1).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-gray-800">{review.user}</span>
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    size={14}
                                    className={i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                                  />
                                ))}
                              </div>
                              {review.verified && (
                                <CheckCircle className="text-green-500" size={14} />
                              )}
                            </div>
                            <p className="text-gray-600 text-sm">{review.comment}</p>
                            <p className="text-xs text-gray-500 mt-2">{review.date}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Codes Tab */}
              {activeTab === 'codes' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Verification Codes</h3>
                    <button
                      onClick={generateVerificationCode}
                      className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all"
                    >
                      <RefreshCw size={16} />
                      Generate New Code
                    </button>
                  </div>

                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="space-y-4">
                      {verificationCodes.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="text-gray-400" size={32} />
                          </div>
                          <h4 className="text-lg font-semibold text-gray-800 mb-2">No Codes Generated</h4>
                          <p className="text-gray-600">Generate verification codes to give to your customers</p>
                        </div>
                      ) : (
                        verificationCodes.map(code => (
                          <div key={code.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                                <Lock className="text-white" size={20} />
                              </div>
                              <div>
                                <p className="font-mono font-semibold text-lg">{code.code}</p>
                                <p className="text-sm text-gray-500">
                                  Generated {new Date(code.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => copyToClipboard(code.code)}
                                className="p-2 text-gray-500 hover:text-gray-700"
                              >
                                <Copy size={16} />
                              </button>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                code.used 
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {code.used ? 'Used' : 'Active'}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">How Verification Codes Work</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Generate unique codes for each customer</li>
                      <li>• Share codes with customers after purchase</li>
                      <li>• Customers use codes to verify their reviews</li>
                      <li>• Verified reviews get special badges</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Reviews Tab */}
              {activeTab === 'reviews' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">All Reviews</h3>
                    <div className="flex gap-2">
                      <select className="border border-gray-300 rounded-lg px-3 py-2">
                        <option>All Reviews</option>
                        <option>Verified Only</option>
                        <option>Unverified Only</option>
                      </select>
                      <select className="border border-gray-300 rounded-lg px-3 py-2">
                        <option>Most Recent</option>
                        <option>Highest Rated</option>
                        <option>Lowest Rated</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {reviews.map(review => (
                      <div key={review.id} className="bg-white rounded-xl p-6 shadow-lg">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {review.user.charAt(1).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-800">{review.user}</h4>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      size={14}
                                      className={i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                                    />
                                  ))}
                                </div>
                                {review.verified && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircle className="text-green-500" size={14} />
                                    <span className="text-xs text-green-600">Verified</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">{review.date}</span>
                        </div>
                        <p className="text-gray-600 mb-4">{review.comment}</p>
                        {review.verificationCode && (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Lock size={14} />
                            <span>Code: {review.verificationCode}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-gray-800 mb-4">Rating Distribution</h4>
                      <div className="space-y-3">
                        {[5, 4, 3, 2, 1].map(rating => {
                          const count = reviews.filter(r => r.rating === rating).length;
                          const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                          return (
                            <div key={rating} className="flex items-center gap-3">
                              <div className="flex items-center gap-1 w-8">
                                <span className="text-sm font-semibold">{rating}</span>
                                <Star className="text-yellow-400" size={12} />
                              </div>
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-yellow-400 h-2 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600 w-8">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-lg">
                      <h4 className="font-semibold text-gray-800 mb-4">Verification Status</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="text-green-500" size={20} />
                            <span className="font-semibold text-green-700">Verified Reviews</span>
                          </div>
                          <span className="font-bold text-green-700">
                            {reviews.filter(r => r.verified).length}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="font-semibold text-gray-700">Unverified Reviews</span>
                          </div>
                          <span className="font-bold text-gray-700">
                            {reviews.filter(r => !r.verified).length}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 